import { FC, Fragment, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronDownIcon, XIcon } from '@heroicons/react/outline';
import InputGroup from '../../../../components/InputGroup';
import TextAreaGroup from '../../../../components/TextAreaGroup';
import Button from '../../../../components/Button';
import PatientSelector from '../../../../components/PatientSelector';
import Avatar from '../../../../components/Avatar';
import {
  CommonWhenType,
  CreateManualClientSessionInput,
  Patient,
  ProviderUserMember,
  ProviderUserRole,
} from '../../../../../generated/graphql';
import { USER_LOCAL_TIMEZONE } from '../../../../lib/time';
import { useFilteredProviderUsers } from '../../hooks/useFilteredProviderUsers';
import { useAuth } from '../../../../../contexts/AuthContext';

interface CreateSessionFormData {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  date: string;
  location?: string;
  patient: Patient | null;
  practitioners: ProviderUserMember[];
}

interface CreateSessionFormProps {
  onSubmit: (data: CreateManualClientSessionInput) => Promise<{
    success: boolean;
    errors?: Array<{ field: string; message: string }>;
  }>;
  onCancel: () => void;
  isSubmitting: boolean;
  initialPatient?: Patient | null;
}

const CreateSessionForm: FC<CreateSessionFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
  initialPatient = null,
}) => {
  const { authedProviderUser } = useAuth();
  const [practitionerQuery, setPractitionerQuery] = useState('');

  const isPractitionerRole =
    authedProviderUser?.role === ProviderUserRole.Practitioner;

  const {
    register,
    handleSubmit,
    clearErrors,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateSessionFormData>({
    defaultValues: {
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0], // Today's date
      startTime: '',
      endTime: '',
      location: '',
      patient: initialPatient || null,
      practitioners: [],
    },
  });

  // Depend on the stable id rather than the full authedProviderUser reference, which may
  // be recreated on every render by useAuth and would otherwise re-run this effect needlessly.
  const authedProviderUserId = authedProviderUser?.id;
  useEffect(() => {
    if (!isPractitionerRole || !authedProviderUser) {
      return;
    }
    setValue('practitioners', [
      {
        __typename: 'ProviderUserMember' as const,
        id: authedProviderUser.id,
        name: authedProviderUser.name ?? '',
        email: authedProviderUser.email ?? '',
        profileImageMedia: authedProviderUser.profileImageMedia,
        patientDataPermission: authedProviderUser.patientDataPermission,
      },
    ] as ProviderUserMember[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPractitionerRole, authedProviderUserId, setValue]);

  const watchPractitioners = watch('practitioners');

  const watchedPractitionerIds = watchPractitioners
    ?.map((practitioner) => practitioner.id)
    ?.filter(Boolean);

  const { filteredProviderUsers } = useFilteredProviderUsers({
    searchQuery: practitionerQuery,
    selectedProviderUserIds: watchedPractitionerIds,
  });

  const handleFormSubmit = async (formData: CreateSessionFormData) => {
    try {
      // Format the data according to the GraphQL schema
      const input = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        patientId: formData.patient.id,
        when: {
          type: CommonWhenType.Timespan,
          startTime: new Date(
            `${formData.date}T${formData.startTime}`,
          ).toISOString(),
          endTime: new Date(
            `${formData.date}T${formData.endTime}`,
          ).toISOString(),
          startTimezone: USER_LOCAL_TIMEZONE,
          endTimezone: USER_LOCAL_TIMEZONE,
        },
        practitionerIds: formData.practitioners.map((p) => p.id),
      };

      const result = await onSubmit(input);

      // If submission failed, don't let the form consider it "submitted"
      if (!result.success) {
        throw new Error(
          result.errors?.[0]?.message || 'Failed to create session',
        );
      }

      return result;
    } catch (err) {
      console.error('Error formatting session creation:', err);
      return {
        success: false,
        errors: [{ field: 'root', message: 'Failed to create session' }],
      };
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="mb-6 text-center">
        <h2 className="font-serif text-subtitle text-green-150">
          Create Session
        </h2>
        <p className="text-caption text-neutral-125">
          Enter the details for the client administration session.
        </p>
      </div>

      <div className="space-y-4">
        <InputGroup
          label="Title"
          {...register('title', { required: 'Title is required' })}
          errorMessage={errors.title?.message}
          useNaturalLettering
        />

        <TextAreaGroup
          label="Description"
          {...register('description')}
          errorMessage={errors.description?.message}
        />

        <InputGroup
          label="Location"
          {...register('location')}
          errorMessage={errors.location?.message}
          useNaturalLettering
        />

        <div className="space-y-2">
          <label className="text-caption font-medium text-green-150">
            Client <span className="text-red-100">*</span>
          </label>
          <Controller
            name="patient"
            control={control}
            rules={{ required: 'Please select a client' }}
            render={({ field: { onChange, value } }) => (
              <PatientSelector
                onChange={onChange}
                value={value}
                placeholder="Search for a client"
                errorMessage={errors.patient?.message}
                required
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <label className="text-caption font-medium text-green-150">
            Practitioners <span className="text-red-100">*</span>
          </label>
          <Controller
            name="practitioners"
            control={control}
            rules={{
              validate: (value) =>
                value.length > 0 || 'At least one practitioner is required',
            }}
            render={({ field: { onChange, value } }) => {
              const togglePractitioner = (practitioner: ProviderUserMember) => {
                const isSelected = value.some((p) => p.id === practitioner.id);
                if (isSelected) {
                  onChange(value.filter((p) => p.id !== practitioner.id));
                } else {
                  onChange([...value, practitioner]);
                }
                setPractitionerQuery('');
              };

              return (
                <>
                  {!isPractitionerRole && (
                    <Combobox
                      as="div"
                      className="relative w-full text-body"
                      value={null}
                      onChange={(practitioner: ProviderUserMember | null) => {
                        if (practitioner) {
                          togglePractitioner(practitioner);
                        }
                      }}
                      immediate
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={classNames(
                            'relative flex w-full cursor-default flex-row items-center justify-start text-body placeholder:text-body',
                            'max-h-14 rounded-md bg-neutral-50 p-4 text-left text-secondary-150 shadow-sm sm:text-caption',
                            'border-0 placeholder:text-neutral-110 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50',
                            'selection:bg-secondary-20',
                          )}
                          onChange={(event) =>
                            setPractitionerQuery(event.target.value)
                          }
                          placeholder="Search practitioners..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronDownIcon
                            className="h-5 w-5 text-neutral-125"
                            aria-hidden="true"
                          />
                        </Combobox.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setPractitionerQuery('')}
                      >
                        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
                          {filteredProviderUsers.map((providerUser) => (
                            <Combobox.Option
                              key={providerUser.id}
                              value={providerUser}
                            >
                              {({ active, selected }) => (
                                <div
                                  className={classNames(
                                    'relative flex cursor-pointer select-none flex-row items-center py-3 px-5 text-green-150 hover:bg-neutral-25',
                                    {
                                      'bg-neutral-25': selected || active,
                                    },
                                  )}
                                >
                                  <div className="flex min-w-0 flex-1 items-center">
                                    <Avatar
                                      size="small"
                                      name={
                                        providerUser.name || providerUser.email
                                      }
                                      imageUrl={
                                        providerUser?.profileImageMedia?.url ||
                                        undefined
                                      }
                                      className="mr-3"
                                    />
                                    <div className="flex min-w-0 flex-col">
                                      <span className="text-sm truncate font-medium text-neutral-150">
                                        {providerUser.name ||
                                          providerUser.email}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Combobox.Option>
                          ))}
                        </Combobox.Options>
                      </Transition>
                    </Combobox>
                  )}

                  <div
                    className={classNames(
                      'flex flex-wrap gap-2 rounded-md p-2',
                      value.length > 0 ? 'min-h-[48px]' : 'min-h-0',
                    )}
                  >
                    {value.map((practitioner) =>
                      isPractitionerRole ? (
                        <div
                          key={practitioner.id}
                          className="inline-flex max-w-full items-center gap-2 rounded-full bg-neutral-25 px-3 py-1 text-small-caption"
                        >
                          <Avatar
                            imageUrl={
                              practitioner.profileImageMedia?.url || undefined
                            }
                            name={practitioner.name || practitioner.email}
                            size="small"
                          />
                          <span className="max-w-[200px] truncate text-neutral-150">
                            {practitioner.name || practitioner.email}
                          </span>
                        </div>
                      ) : (
                        <button
                          key={practitioner.id}
                          type="button"
                          className="inline-flex max-w-full items-center gap-2 rounded-full bg-neutral-25 px-3 py-1 text-small-caption"
                          onClick={() => togglePractitioner(practitioner)}
                        >
                          <Avatar
                            imageUrl={
                              practitioner.profileImageMedia?.url || undefined
                            }
                            name={practitioner.name || practitioner.email}
                            size="small"
                          />
                          <span className="max-w-[200px] truncate text-neutral-150">
                            {practitioner.name || practitioner.email}
                          </span>
                          <XIcon className="h-3 w-3 text-neutral-125" />
                        </button>
                      ),
                    )}
                  </div>
                </>
              );
            }}
          />
          {errors.practitioners && (
            <p className="mt-1 text-small-caption text-red-100">
              {errors.practitioners.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <InputGroup
            label="Date"
            type="date"
            {...register('date', { required: 'Date is required' })}
            errorMessage={errors.date?.message}
            useNaturalLettering
          />
          <InputGroup
            label="Start Time"
            type="time"
            {...register('startTime', { required: 'Start time is required' })}
            errorMessage={errors.startTime?.message}
            useNaturalLettering
          />
          <InputGroup
            label="End Time"
            type="time"
            {...register('endTime', { required: 'End time is required' })}
            errorMessage={errors.endTime?.message}
            useNaturalLettering
          />
        </div>

        <div className="flex justify-center space-x-4 pt-4">
          <Button
            title="Cancel"
            onClick={onCancel}
            theme="secondary-white"
            size="medium"
            disabled={isSubmitting}
          />
          <Button
            title={isSubmitting ? 'Creating...' : 'Create Session'}
            disabled={isSubmitting}
            onClick={() => {
              clearErrors();
              handleSubmit(handleFormSubmit);
            }}
            type="submit"
            theme="primary"
            size="medium"
          />
        </div>
      </div>
    </form>
  );
};

export default CreateSessionForm;
